/**
 * Status Helper Utilities
 * 
 * Provides standardized status objects for tracking operation states
 * across the application. Status messages are user-friendly and non-technical.
 */

/**
 * Creates a status object for tracking operation states
 * @param {string} status - One of: 'loading', 'success', 'error', 'idle'
 * @param {string} message - User-friendly message describing the current state
 * @param {string} operation - Name of the operation (e.g., 'culture_audit_reports', 'debrief_schedules')
 * @returns {Object} Status object
 */
export const createStatus = (status, message, operation) => {
  return {
    status, // 'loading' | 'success' | 'error' | 'idle'
    message, // User-friendly message
    operation, // Operation identifier
    timestamp: Date.now(),
  };
};

/**
 * Predefined status messages for common operations
 * These are user-friendly and non-technical
 */
export const STATUS_MESSAGES = {
  // Culture Audit Reports
  CULTURE_AUDIT_LOADING: 'Loading data...',
  CULTURE_AUDIT_SUCCESS: 'Data loaded',
  CULTURE_AUDIT_ERROR: 'Failed to load data',
  CULTURE_AUDIT_PROCESSING: 'Loading data...',
  CULTURE_AUDIT_COMPLETED: 'Data ready',
  CULTURE_AUDIT_CACHE_LOADING: 'Preparing data for analysis...',
  
  // Debrief Schedules
  DEBRIEF_SCHEDULES_LOADING: 'Loading data...',
  DEBRIEF_SCHEDULES_SUCCESS: 'Data loaded',
  DEBRIEF_SCHEDULES_ERROR: 'Failed to load data',
  
  // General
  LOADING: 'Loading...',
  SUCCESS: 'Operation completed successfully',
  ERROR: 'An error occurred',
  IDLE: 'Ready',
};

/**
 * Helper to create loading status
 */
export const createLoadingStatus = (operation, customMessage = null) => {
  const message = customMessage || STATUS_MESSAGES[`${operation.toUpperCase()}_LOADING`] || STATUS_MESSAGES.LOADING;
  return createStatus('loading', message, operation);
};

/**
 * Helper to create success status
 */
export const createSuccessStatus = (operation, customMessage = null) => {
  const message = customMessage || STATUS_MESSAGES[`${operation.toUpperCase()}_SUCCESS`] || STATUS_MESSAGES.SUCCESS;
  return createStatus('success', message, operation);
};

/**
 * Helper to create error status
 */
export const createErrorStatus = (operation, customMessage = null) => {
  const message = customMessage || STATUS_MESSAGES[`${operation.toUpperCase()}_ERROR`] || STATUS_MESSAGES.ERROR;
  return createStatus('error', message, operation);
};

/**
 * Helper to create idle status
 */
export const createIdleStatus = (operation) => {
  return createStatus('idle', STATUS_MESSAGES.IDLE, operation);
};

/**
 * Helper to extract user-friendly error message from error object
 */
export const extractErrorMessage = (error) => {
  if (!error) return 'An unexpected error occurred';
  
  // Try to extract user-friendly message from various error formats
  if (typeof error === 'string') return error;
  if (error.message) return error.message;
  if (error.detail) return error.detail;
  if (error.data?.detail) return error.data.detail;
  if (error.response?.data?.detail) return error.response.data.detail;
  if (error.response?.data?.message) return error.response.data.message;
  
  return 'An error occurred while processing your request';
};

